import { checkAttributesExist } from "#lib/utils/misc.js";
import { findVariantId } from "#lib/utils/product.js";
import { getCartAttributes } from "#lib/utils/tracking-attributes.js";

/**
 * Add a variant to the cart via /api/cart/add.
 *
 * `context` ({ pathname, edition }) is forwarded so the server's own failure
 * events (add_to_cart_failed, cart_line_zeroed) can be located in PostHog -
 * server events carry no $pathname of their own.
 *
 * On failure the thrown Error carries the server's message plus `status`,
 * `regionUnavailable`, `soldOut` and `country`, so the UI can tell "we don't
 * ship to your country" (409) apart from a transient error. Before this, the
 * client read `errorData.message` while the endpoint returns `{ error }`, so
 * every failure - including a whole country being unable to buy - collapsed
 * into "Failed to add to cart".
 */
export async function addToCart(
  cartId,
  variantId,
  quantity,
  countryCode = null,
  context = {},
) {
  try {
    const trackingAttributes = getCartAttributes();

    // Add item to cart - the API endpoint handles cart creation if needed.
    // When there's no cartId yet, the server applies the attributes at cart
    // creation; for a reused cart we refresh them below.
    const response = await fetch("/api/cart/add", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        cartId,
        variantId,
        quantity,
        // Applied at creation when the server makes the cart, so the line is
        // evaluated in the right market rather than the default one.
        countryCode,
        // Always send attributes: the server only applies them when it
        // CREATES a cart - at first add, or when recovering from a dead
        // persisted cart id. For a reused live cart they're refreshed below.
        attributes: trackingAttributes,
        // Telemetry context only - never trusted for cart contents.
        ...(typeof context?.pathname === "string"
          ? { pathname: context.pathname }
          : {}),
        ...(typeof context?.edition === "string"
          ? { edition: context.edition }
          : {}),
      }),
    });

    if (!response.ok) {
      let errorData = {};
      try {
        errorData = await response.json();
      } catch {
        /* non-JSON body (edge/proxy error page) - fall back to the generic message */
      }
      // `error` is what the endpoint returns; `message` is kept as a fallback
      // for any older shape still in flight during a deploy.
      const err = new Error(
        errorData.error || errorData.message || "Failed to add to cart",
      );
      err.status = response.status;
      err.regionUnavailable = errorData.regionUnavailable === true;
      err.soldOut = errorData.soldOut === true;
      err.country = errorData.country ?? null;
      throw err;
    }

    const data = await response.json();
    // console.log('Cart response data:', data);

    if (!data.cart) {
      console.error("Invalid cart response:", data);
      throw new Error("Invalid cart response from server");
    }

    // The reused-cart attribution refresh used to happen here, writing the
    // whole attribute set straight to Shopify from the browser. /api/cart/add
    // does it now: the browser was the last writer of a set that includes the
    // posthog_distinct_id pin, so it could name any visitor - and it replaced
    // the pin the server had just stamped. Same behaviour, one trusted writer.

    // Return the updated cart items
    return {
      cartId: data.cart.id, // Use the cart ID from the response
      // The mutation's own payload already carries the checkout URL and the
      // resulting lines, so a caller heading straight to checkout does not
      // have to read the cart back - that read can land before the write is
      // visible and come back without a checkoutUrl.
      checkoutUrl: data.cart.checkoutUrl || data.checkoutUrl || null,
      lines: data.cart.lines?.edges || [],
      items:
        data.cart.lines?.edges?.map((edge) => ({
          id: edge.node.id,
          variantId: edge.node.merchandise.id,
          quantity: edge.node.quantity,
          title: edge.node.merchandise.product.title,
          price: edge.node.cost.totalAmount.amount,
          image:
            edge.node.merchandise.product.images?.edges[0]?.node?.originalSrc,
        })) || [],
    };
  } catch (error) {
    console.error("Error adding to cart:", error);
    throw error;
  }
}

export async function fetchCart(cartId) {
  if (!cartId) return { items: [] };

  try {
    // encodeURIComponent, because a Shopify cart id is "gid://shopify/Cart/xyz"
    // and those slashes would make this a multi-segment path that the
    // [cartId] route never matches - a 404, not a cart. This helper was dead
    // code until 2026-09-23, which is why nothing had ever hit it; the "builds
    // an unroutable URL" note in docs/DEEP-REVIEW-TODO.md #30 was right.
    // SvelteKit decodes the param again on the other side.
    const response = await fetch(`/api/cart/${encodeURIComponent(cartId)}`);
    if (!response.ok) {
      throw new Error(`Failed to fetch cart (HTTP ${response.status})`);
    }
    const { cart } = await response.json();
    return cart;
  } catch (error) {
    console.error("Error fetching cart:", error);
    // Rethrow the original. Replacing it with a flat "Failed to fetch cart"
    // is what hid this class of failure for so long: the signature that
    // identifies it - "Load failed" on WebKit, "Failed to fetch" on Chrome, a
    // JSON parse error when something answers HTML - lives in the message, and
    // cart_init_failed is counted by it (hub ledger 2026-09-23-069).
    throw error;
  }
}

export async function updateCartItem(cartId, lineId, quantity) {
  try {
    const response = await fetch("/api/cart/update", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        cartId,
        lineId,
        quantity,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || "Failed to update cart item");
    }

    const data = await response.json();
    // console.log('Cart response data:', data);

    if (!data.cart) {
      console.error("Invalid cart response:", data);
      throw new Error("Invalid cart response from server");
    }

    return data;
  } catch (error) {
    console.error("Error updating cart item:", error);
    throw error;
  }
}

export async function removeFromCart(cartId, lineId) {
  try {
    const response = await fetch("/api/cart/remove", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        cartId,
        lineId,
      }),
    });

    if (!response.ok) {
      throw new Error("Failed to remove cart item");
    }

    const { cart } = await response.json();
    return cart;
  } catch (error) {
    console.error("Error removing cart item:", error);
    throw new Error("Failed to remove cart item");
  }
}
